import { IndexParam, RenderSegment, RenderSegments, SegmentedClauseParam, SegmentedText, segmentedTextStyle, SegmentedTextType } from "../domain/types/ClauseParams";
import { BeneficialCompanyEntity, BeneficialEntity, BeneficialMinorEntity, BeneficialPersonEntity, ContractEntity } from "../domain/entities";
import { getDeepValue } from "../utils/object";
import { pathHead } from "../utils/string";
import { isValidInputValue } from "./CompletionPercentage";
import { LanguageType } from "../contexts/TranslationProvider";
import { beneficialMacros, virtualbeneficialGetters } from "./Beneficial";
import moment from "moment";
import { splitArrayToMatrix } from "../utils/array";
import { text } from "stream/consumers";
import { evaluateParamFormat } from "../domain/Form";

type Scope = {
	beneficialParamName?: string;
	beneficial?: BeneficialEntity;
}
function countIndex(indexParam: IndexParam, start?: number) {
	if (start == undefined || start == null) return indexParam.args.start
	return start + indexParam.args.step
}
export function getValueFromInputs<T extends SegmentedClauseParam>(inputPath: string, inputs: any,
	fileNames: ContractEntity['fileNames'], beneficialsMap: ContractEntity['beneficialsMap'],
	params: T[], scope?: Scope) {
	const [paramName, valuePath] = pathHead(inputPath)
	let parentParam = params.find((param) => param.name === paramName)
	let value = null
	if (parentParam) {
		switch (parentParam.type) {
			case 'boolean':
			case 'enum':
				const definitions = params.filter((param) => param.name === paramName && param.type == parentParam.type)
				parentParam = definitions.find((param) => ((param as any).definition || 0) === (Number(valuePath) || 0))
				value = inputs[paramName]
				break;
			case 'static-table':
				const table = params.find((param) => param.name === paramName)
				value = table
				break;
			case 'beneficial':
				const benecialId = inputs[paramName]
				value = beneficialsMap[benecialId]
				break;
			case 'beneficial[]':
				const benecialsId = inputs[paramName]
				value = Array.isArray(benecialsId) ? benecialsId.map(benecialId => beneficialsMap[benecialId])
					: undefined
				break;
			case 'file':
				if (inputs[paramName] instanceof File) {
					value = inputs[paramName].name
				} else {
					const fileId = inputs[paramName]
					value = fileNames[fileId]
				}
				break;
			case 'index':
				value = countIndex(parentParam, inputs[paramName])
				break;
			default:
				value = inputs[paramName]
				break;
		}
	} else if (scope?.beneficial && Object.keys(scope.beneficial).includes(paramName)) {
		parentParam = params.find((param) => param.name === scope?.beneficialParamName)
		value = getDeepValue(scope.beneficial, inputPath)
		return { value, parentParam, scopeParam: true };

	}
	return { value, parentParam };
};
export function findParamBoundaries(renderSegments: RenderSegments, paramName: string): [number, number][] {
	const boundaries: [number, number][] = [];
	let paramStack: { index: number, paramName: string }[] = [];

	for (let i = 0; i < renderSegments.length; i++) {
		const segment = renderSegments[i];

		if (segment.type === SegmentedTextType.PARAM_START && segment.paramName === paramName) {
			paramStack.push({ index: i, paramName });
		} else if (segment.type === SegmentedTextType.PARAM_END && segment.paramName === paramName && paramStack.length > 0 && paramStack[paramStack.length - 1].paramName === paramName) {
			const start = paramStack.pop()!.index;
			boundaries.push([start, i]);
		}
	}

	return boundaries;
}

export function findParamsBoundaries(renderSegments: RenderSegments): [number, number][] {
	const boundaries: [number, number][] = [];
	let paramStack: { index: number, paramName: string }[] = [];

	for (let i = 0; i < renderSegments.length; i++) {
		const segment = renderSegments[i];

		if (segment.type === SegmentedTextType.PARAM_START) {
			paramStack.push({ index: i, paramName: segment.paramName });
		} else if (segment.type === SegmentedTextType.PARAM_END && paramStack.length > 0 && paramStack[paramStack.length - 1].paramName === segment.paramName) {
			const start = paramStack.pop()!.index;
			boundaries.push([start, i]);
		}
	}

	return boundaries;
}

function prepareSegmentedTextExport(segmentedText: SegmentedText): SegmentedText {
	if (!segmentedText) return []
	if (segmentedText.length == 1 && segmentedText[0][2] == SegmentedTextType.STATIC && segmentedText[0][1] == "_") {
		// return a static segment with space as value
		return [[segmentedText[0][0], " ", SegmentedTextType.STATIC, segmentedText[0][3]]]
	}
	return segmentedText
}

export function getTableSegments(rows: RenderSegment[][], defaultSegment: RenderSegment, { paramName, style, cellStyle }: {
	paramName?: string;
	style?: segmentedTextStyle;
	cellStyle?: segmentedTextStyle;
} = {}
): RenderSegments {
	let outputSegmentedText = [] as RenderSegments
	// Start of the static table
	outputSegmentedText.push({
		type: SegmentedTextType.STATIC_TABLE_START,
		id: `${defaultSegment.id}-TABLE-START`,
		paramName,
		style,
		value: " ",
	});

	rows.forEach((row, rowIndex) => {
		// Start of the row
		outputSegmentedText.push({
			type: SegmentedTextType.STATIC_TABLE_ROW_START,
			id: `${defaultSegment.id}-ROW-${rowIndex}-START`,
			paramName,
			value: " "
		});

		row.forEach((cell, cellIndex) => {
			// Start of the cell
			outputSegmentedText.push({
				type: SegmentedTextType.STATIC_TABLE_CELL_START,
				id: `${defaultSegment.id}-ROW-${rowIndex}-CELL-${cellIndex}-START`,
				paramName,
				value: " ",
				rowIdx: rowIndex,
				cellIdx: cellIndex,
				style: cellStyle ? cellStyle : cell.style
			});
			outputSegmentedText.push(cell)

			// End of the cell
			outputSegmentedText.push({
				type: SegmentedTextType.STATIC_TABLE_CELL_END,
				id: `${defaultSegment.id}-ROW-${rowIndex}-CELL-${cellIndex}-END`,
				paramName,
				value: " ",
				style: cellStyle ? cellStyle : cell.style
			});
		});

		// End of the row
		outputSegmentedText.push({
			type: SegmentedTextType.STATIC_TABLE_ROW_END,
			id: `${defaultSegment.id}-ROW-${rowIndex}-END`,
			paramName,
			value: " "
		});
	});

	// End of the static table
	outputSegmentedText.push({
		type: SegmentedTextType.STATIC_TABLE_END,
		id: `${defaultSegment.id}-TABLE-END`,
		paramName,
		value: " ",
		inputValue: ""
	});
	return outputSegmentedText
}

export function getRenderSegments(segmentedText: SegmentedText, inputs: any,
	fileNames: ContractEntity['fileNames'], beneficialsMap: ContractEntity['beneficialsMap'],
	params: SegmentedClauseParam[], t: (key: any) => any, language: LanguageType, scope?: Scope,
	docExport: boolean = false,
	indexCounters: Record<string, number> = {}
): RenderSegments {
	let outputSegmentedText = [] as RenderSegments
	segmentedText.map((segment) => {
		segment[3] = segment[3] || {}
		segment[3].textAlign = segment[3].textAlign ?? "justify"
		switch (segment[2]) {
			case SegmentedTextType.LIST_ITEM_START:
				outputSegmentedText.push({
					type: SegmentedTextType.LIST_ITEM_START,
					id: segment[0],
					value: segment[1],
					style: segment[3]
				})
				break;
			case SegmentedTextType.PARAGRAPH_START:
				outputSegmentedText.push({
					type: SegmentedTextType.PARAGRAPH_START,
					id: segment[0],
					value: segment[1],
					style: segment[3],
				})
				break;
			case SegmentedTextType.STATIC:
				outputSegmentedText.push({
					type: SegmentedTextType.STATIC,
					id: segment[0],
					value: segment[1],
					style: segment[3]
				})
				break;
			case SegmentedTextType.PARAM:
				const inputPath = segment[1];
				const [paramName, valuePath] = pathHead(inputPath)
				const { value: input, parentParam, scopeParam } = getValueFromInputs(inputPath, inputs, fileNames, beneficialsMap, params, scope);
				if (!parentParam) {
					// console.warn("getRenderSegments: parentParam not found", inputPath, segment);
					return outputSegmentedText.push({
						type: SegmentedTextType.STATIC,
						id: segment[0],
						value: segment[1],
						style: segment[3]
					})
				}
				if (scopeParam) {
					return outputSegmentedText.push(
						{
							type: SegmentedTextType.PARAM_VALUE,
							id: segment[0],
							value: input,
							paramName: parentParam.name,
							style: segment[3]
						}
					)
				}
				if (!isValidInputValue(input)) {
					return outputSegmentedText.push({
						type: SegmentedTextType.PARAM,
						id: segment[0],
						value: segment[1],
						paramName: parentParam.name,
						style: segment[3]
					})
				}
				const defaultSegment = {
					type: SegmentedTextType.PARAM,
					id: segment[0],
					value: segment[1],
					paramName: parentParam.name,
					style: segment[3]
				}
				const startSegment = {
					type: SegmentedTextType.PARAM_START,
					id: `${defaultSegment.id}-START`,
					paramName: defaultSegment.paramName,
					value: " ",
					inputValue: input
				}
				const endSegment = {
					type: SegmentedTextType.PARAM_END,
					id: `${defaultSegment.id}-END`,
					paramName: defaultSegment.paramName,
					value: " ",
					inputValue: input
				}
				switch (parentParam.type) {
					case 'index':
						const indexParam = parentParam as IndexParam
						const count = countIndex(indexParam, indexCounters[parentParam.name])
						indexCounters[parentParam.name] = count
						return outputSegmentedText.push({
							type: SegmentedTextType.PARAM_VALUE,
							id: segment[0],
							value: `${count}`,
							paramName: parentParam.name,
							style: segment[3]
						})
					case 'beneficial':
						const beneficial = input as BeneficialEntity
						const fieldName = segment[1].split(".")[1] as keyof BeneficialPersonEntity | keyof BeneficialCompanyEntity | keyof BeneficialMinorEntity
						if (fieldName == 'address') {
							return outputSegmentedText.push(
								{
									type: SegmentedTextType.PARAM_VALUE,
									id: segment[0],
									value: beneficial.addressString,
									paramName: parentParam.name,
									style: segment[3]
								}
							)
						} else if (fieldName == 'description') {
							return outputSegmentedText.push({
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: beneficialMacros.descritpion(beneficial, t, language),
								paramName: parentParam.name,
								style: segment[3]
							})
						} else if (fieldName == 'fullName') {
							return outputSegmentedText.push({
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: beneficialMacros.fullName(beneficial, t, language),
								paramName: parentParam.name,
								style: segment[3]
							})
						} else if (fieldName == 'signature') {
							return outputSegmentedText.push({
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: beneficialMacros.signature(beneficial, t, language),
								paramName: parentParam.name,
								style: segment[3]
							})
						}
						else if (fieldName == 'gender') {
							return outputSegmentedText.push({
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: virtualbeneficialGetters.honorific(beneficial, t, language),
								paramName: parentParam.name,
								style: segment[3]
							})
						}
						else if (fieldName == 'minorGender') {
							return outputSegmentedText.push({
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: t(`pages.editionContract.popups.benificial.genderPrefix.${beneficial?.gender}`),
								paramName: parentParam.name,
								style: segment[3]
							})
						}
						else if (fieldName == "nationality") {
							return outputSegmentedText.push({
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: virtualbeneficialGetters.nationality(beneficial, t, language),
								paramName: parentParam.name,
								style: segment[3]
							})
						}
						else if (fieldName == "minorNationality") {
							return outputSegmentedText.push({
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: virtualbeneficialGetters.minorNationality((beneficial) as BeneficialMinorEntity, t, language),
								paramName: parentParam.name
							})
						}
						else if (fieldName == "maritalStatus") {
							return outputSegmentedText.push({
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: t(`pages.editionContract.popups.benificial.options.maritalStatusOptions.${beneficial?.maritalStatus}`),
								paramName: parentParam.name,
								style: segment[3]
							})
						}
						else if (fieldName == "socialCapitalCurrency") {
							return outputSegmentedText.push({
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: virtualbeneficialGetters.socialCapitalCurrency(beneficial as any, t, language),
								paramName: parentParam.name,
								style: segment[3]
							})
						}
						const value = getDeepValue(beneficial, valuePath)
						if (!isValidInputValue(value)) {
							return outputSegmentedText.push({
								type: SegmentedTextType.PARAM,
								id: segment[0],
								value: segment[1],
								paramName: parentParam.name,
								style: segment[3]
							})
						}
						if (fieldName == 'cinDeliveryDate'
							|| fieldName == 'dateOfBirth'
							|| fieldName == 'createdAt'
							|| fieldName == 'updatedAt'
							|| fieldName == 'minorDateOfBirth'
							|| fieldName == 'minorOrderDate'
						) {
							return outputSegmentedText.push({
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: moment(new Date(value)).format(language == 'ar' ? "YYYY/MM/DD" : "DD/MM/YYYY"),
								paramName: parentParam.name,
								style: segment[3]
							})
						}
						return outputSegmentedText.push({
							type: SegmentedTextType.PARAM_VALUE,
							id: segment[0],
							value: String(value),
							paramName: parentParam.name,
							style: segment[3]
						})
					case 'beneficial[]':
						const BeneficialListFieldName = segment[1].split(".")[1] as keyof BeneficialPersonEntity | keyof BeneficialCompanyEntity | keyof BeneficialMinorEntity
						const beneficials: BeneficialEntity[] = Array.isArray(input) && input.filter(b => b) || []

						if (BeneficialListFieldName == 'signature') {
							const _signatures: string[] = []
							beneficials.forEach((beneficial) => {
								const signature = beneficialMacros.signature(beneficial, t, language)
								_signatures.push(...signature.split('\n\n\n').filter(s => s))
							})
							let signatures: string[] = []
							_signatures.forEach(sig => signatures.push(sig, ""))
							switch (signatures.length % 3) {
								case 1:
									signatures.pop()
									break
								case 2:
									signatures = ["", ...signatures]
									break
							}
							const renderCells = signatures.map((sig) => ({
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: sig,
								paramName: parentParam.name,
								style: segment[3]
							}))
							let rows = splitArrayToMatrix(renderCells, 3)

							return outputSegmentedText.push(...getTableSegments(rows, defaultSegment, {
								paramName: parentParam.name,
								style: { borderColor: 'transparent' },
								cellStyle: { textAlign: 'center' }
							}))

						}

						if (beneficials.length) {
							return beneficials.forEach((beneficial: BeneficialEntity) => {
								if (BeneficialListFieldName == 'address') {
									return outputSegmentedText.push(
										{
											type: SegmentedTextType.PARAM_VALUE,
											id: segment[0],
											value: "\n" + beneficial.addressString,
											paramName: parentParam.name,
											style: segment[3]
										}
									)
								} else if (BeneficialListFieldName == 'description') {
									return outputSegmentedText.push({
										type: SegmentedTextType.PARAM_VALUE,
										id: segment[0],
										value: "\n" + beneficialMacros.descritpion(beneficial, t, language),
										paramName: parentParam.name,
										style: segment[3]
									})
								}
								else if (BeneficialListFieldName == 'fullName') {
									return outputSegmentedText.push({
										type: SegmentedTextType.PARAM_VALUE,
										id: segment[0],
										value: "\n" + beneficialMacros.fullName(beneficial, t, language),
										paramName: parentParam.name,
										style: segment[3]
									})
								}
								else if (BeneficialListFieldName == 'gender') {
									return outputSegmentedText.push({
										type: SegmentedTextType.PARAM_VALUE,
										id: segment[0],
										value: "\n" + virtualbeneficialGetters.honorific(beneficial, t, language),
										paramName: parentParam.name,
										style: segment[3]
									})
								}
								else if (BeneficialListFieldName == 'minorGender') {
									return outputSegmentedText.push({
										type: SegmentedTextType.PARAM_VALUE,
										id: segment[0],
										value: "\n" + t(`pages.editionContract.popups.benificial.genderPrefix.${beneficial?.gender}`),
										paramName: parentParam.name,
										style: segment[3]
									})
								}
								else if (BeneficialListFieldName == "nationality") {
									return outputSegmentedText.push({
										type: SegmentedTextType.PARAM_VALUE,
										id: segment[0],
										value: "\n" + virtualbeneficialGetters.nationality(beneficial, t, language),
										paramName: parentParam.name,
										style: segment[3]
									})
								}
								else if (BeneficialListFieldName == "maritalStatus") {
									return outputSegmentedText.push({
										type: SegmentedTextType.PARAM_VALUE,
										id: segment[0],
										value: "\n" + t(`pages.editionContract.popups.benificial.options.maritalStatusOptions.${beneficial?.maritalStatus}`),
										paramName: parentParam.name,
										style: segment[3]
									})
								}
								else if (BeneficialListFieldName == "socialCapitalCurrency") {
									return outputSegmentedText.push({
										type: SegmentedTextType.PARAM_VALUE,
										id: segment[0],
										value: "\n" + virtualbeneficialGetters.socialCapitalCurrency(beneficial as any, t, language),
										paramName: parentParam.name,
										style: segment[3]
									})
								}
								const value = getDeepValue(beneficial, valuePath)
								if (!isValidInputValue(value)) {
									return outputSegmentedText.push({
										type: SegmentedTextType.PARAM,
										id: segment[0],
										value: "\n" + segment[1],
										paramName: parentParam.name,
										style: segment[3]
									})
								}
								if (BeneficialListFieldName == 'cinDeliveryDate'
									|| BeneficialListFieldName == 'dateOfBirth'
									|| BeneficialListFieldName == 'createdAt'
									|| BeneficialListFieldName == 'updatedAt'
									|| BeneficialListFieldName == 'minorDateOfBirth'
									|| BeneficialListFieldName == 'minorOrderDate'
								) {
									return outputSegmentedText.push({
										type: SegmentedTextType.PARAM_VALUE,
										id: segment[0],
										value: "\n" + moment(new Date(value)).format(language == 'ar' ? "YYYY/MM/DD" : "DD/MM/YYYY"),
										paramName: parentParam.name,
										style: segment[3]
									})
								}
								return outputSegmentedText.push({
									type: SegmentedTextType.PARAM_VALUE,
									id: segment[0],
									value: "\n" + String(value),
									paramName: parentParam.name,
									style: segment[3]
								})

							}
							)
						}
						return outputSegmentedText.push(defaultSegment)
					case 'boolean':
						const content = input ? parentParam.args.textIfTrue : parentParam.args.textIfFalse
						const exportContent = prepareSegmentedTextExport(content)
						return outputSegmentedText.push(
							startSegment,
							...getRenderSegments(docExport ? exportContent : content,
								inputs, fileNames, beneficialsMap, params, t, language, scope, docExport, indexCounters),
							endSegment
						)

					case 'date':
						const reformattedDate = moment(new Date(input)).format(language == 'ar' ? "YYYY/MM/DD" : "DD/MM/YYYY")
						return outputSegmentedText.push(
							{
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: parentParam.format ? String(evaluateParamFormat(parentParam, reformattedDate)) : reformattedDate,
								paramName: parentParam.name,
								style: segment[3]
							}
						)
					case 'file':
						return outputSegmentedText.push({
							type: SegmentedTextType.PARAM_VALUE,
							id: segment[0],
							value: input,
							paramName: parentParam.name,
							style: segment[3]
						})
					case 'comment':
						return outputSegmentedText.push({
							type: SegmentedTextType.PARAM_COMMENT_VALUE,
							id: segment[0],
							value: input,
							paramName: parentParam.name,
							style: segment[3]
						})
					case 'enum':
						const arg = parentParam.args[input]
						if (!arg)
							return outputSegmentedText.push(defaultSegment)
						const segmentedText = arg.text;
						const optionContent = prepareSegmentedTextExport(segmentedText)
						try {
							return outputSegmentedText.push(
								startSegment,
								...getRenderSegments(docExport ? optionContent : segmentedText
									, inputs, fileNames, beneficialsMap, params, t, language, scope, docExport, indexCounters),
								endSegment,
							)
						} catch (error) {
							console.error({ input, segmentedText });
							throw error;
						}
					case 'static-table':
						const tableArg = parentParam.args;
						if (!tableArg.cells || !tableArg.cells.length) {
							return outputSegmentedText.push(defaultSegment);
						}

						// Start of the static table
						outputSegmentedText.push({
							type: SegmentedTextType.STATIC_TABLE_START,
							id: `${defaultSegment.id}-TABLE-START`,
							paramName: defaultSegment.paramName,
							value: " ",
							inputValue: input,
							style: tableArg.style
						});

						const cells = tableArg.cells;

						cells.forEach((row, rowIndex) => {
							// Start of the row
							outputSegmentedText.push({
								type: SegmentedTextType.STATIC_TABLE_ROW_START,
								id: `${defaultSegment.id}-ROW-${rowIndex}-START`,
								paramName: defaultSegment.paramName,
								value: " ",
							});

							row.forEach((cell, cellIndex) => {
								const cellAlignment = cell.cellStyle?.textAlign ?? "justify";
								// Start of the cell
								outputSegmentedText.push({
									type: SegmentedTextType.STATIC_TABLE_CELL_START,
									id: `${defaultSegment.id}-ROW-${rowIndex}-CELL-${cellIndex}-START`,
									paramName: defaultSegment.paramName,
									value: " ",
									style: {
										...tableArg.style,
										textAlign: cellAlignment,
									},
									rowIdx: rowIndex,
									cellIdx: cellIndex,
								});

								const cellValue = cell.content;
								if (cellValue) {
									// Recursively handle nested segments within the cell
									cellValue.forEach((content) => {
										const cellContent = prepareSegmentedTextExport([content])
										outputSegmentedText.push(...getRenderSegments(
											docExport ? cellContent : [content]
											, inputs, fileNames, beneficialsMap, params, t, language, scope, docExport, indexCounters
										));
									});
								}

								// End of the cell
								outputSegmentedText.push({
									type: SegmentedTextType.STATIC_TABLE_CELL_END,
									id: `${defaultSegment.id}-ROW-${rowIndex}-CELL-${cellIndex}-END`,
									paramName: defaultSegment.paramName,
									value: " ",
									style: {
										textAlign: cellAlignment
									},
								});
							});

							// End of the row
							outputSegmentedText.push({
								type: SegmentedTextType.STATIC_TABLE_ROW_END,
								id: `${defaultSegment.id}-ROW-${rowIndex}-END`,
								paramName: defaultSegment.paramName,
								value: " "
							});
						});

						// End of the static table
						outputSegmentedText.push({
							type: SegmentedTextType.STATIC_TABLE_END,
							id: `${defaultSegment.id}-TABLE-END`,
							paramName: defaultSegment.paramName,
							value: " ",
							inputValue: input
						});

						return outputSegmentedText;

					case 'list':
						return outputSegmentedText.push((Array.isArray(input)) ?
							{
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: input.map(idx => parentParam.args[idx]?.option).join("\n"),
								paramName: parentParam.name,
								style: segment[3]
							}
							: defaultSegment)
					case 'table':
						const headers = parentParam.args.map(arg => arg.header)
						return outputSegmentedText.push(
							{
								type: SegmentedTextType.PARAM_TABLE_VALUE,
								id: segment[0],
								value: JSON.stringify([!!parentParam.transposed, [headers, ...input]]),
								paramName: parentParam.name,
								style: segment[3]
							}
						)
					case 'csv':
						return outputSegmentedText.push((Array.isArray(input)) ?
							{
								type: SegmentedTextType.PARAM_TABLE_VALUE,
								id: segment[0],
								value: JSON.stringify([input[0], input[1]]),
								paramName: parentParam.name,
								style: segment[3]
							}
							: defaultSegment)
					case 'number':

						return outputSegmentedText.push(
							{
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: parentParam.format ? String(evaluateParamFormat(parentParam, input)) : input,
								paramName: parentParam.name,
								style: segment[3]
							})

					case 'string':
					case 'property':
						return outputSegmentedText.push(
							{
								type: SegmentedTextType.PARAM_VALUE,
								id: segment[0],
								value: String(input),
								paramName: parentParam.name,
								style: segment[3]
							})
					default:
						console.warn(parentParam);
						console.warn(input);
						console.warn(segment);
						return
				}
			case SegmentedTextType.COMMENT:
				return outputSegmentedText.push({
					type: SegmentedTextType.COMMENT,
					id: segment[0],
					value: segment[1],
					style: segment[3]
				})
			default:
				break;
		}

	})

	return outputSegmentedText.filter(seg => seg).map((seg) =>
	({
		...seg,
		value: String(seg.value)
	})
	);
}